import { useMutation, useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Topbar } from '../components/Topbar'
import { Button, Card, CardPad } from '../components/ui/primitives'
import { trpc } from '../lib/trpc'

/**
 * **„Claude möchte sich verbinden"** – die Freigabe-Seite des OAuth-Wegs
 * ([Z-21](../../../../docs/phasen/zusatz-21-claude-anbindung.md),
 * [E-412](../../../../docs/ENTSCHEIDUNGEN.md#e-412)).
 *
 * claude.ai oder Claude Code schicken den Browser hierher, mit den üblichen Angaben in der
 * Adresse (`client_id`, `redirect_uri`, `code_challenge`, `state`). Wer nicht angemeldet ist,
 * sieht davor die gewohnte Anmeldung und kommt danach auf genau diese Adresse zurück.
 *
 * **Weitergeleitet wird nur an einen Rückweg, den der Server bestätigt hat** – auch die
 * Ablehnung. Sonst wäre diese Seite ein offener Umleiter: ein Link mit fremder `redirect_uri`
 * schickte jeden, der „Ablehnen" klickt, dorthin.
 */
export function ClaudeFreigabe() {
  const p = new URLSearchParams(window.location.search)
  const clientId = p.get('client_id') ?? ''
  const redirectUri = p.get('redirect_uri') ?? ''
  const codeChallenge = p.get('code_challenge') ?? ''
  const methode = p.get('code_challenge_method') ?? ''
  const state = p.get('state') ?? undefined
  const vollstaendig =
    p.get('response_type') === 'code' &&
    clientId &&
    redirectUri &&
    codeChallenge &&
    methode === 'S256'

  const { data: info, error } = useQuery({
    queryKey: ['claude', 'freigabeInfo', clientId, redirectUri],
    queryFn: () => trpc.claude.freigabeInfo.query({ clientId, redirectUri }),
    enabled: Boolean(vollstaendig),
    retry: false,
  })
  const [fehler, setFehler] = useState<string | null>(null)

  const erlauben = useMutation({
    mutationFn: () =>
      trpc.claude.zugangErteilen.mutate({
        clientId,
        redirectUri,
        codeChallenge,
        codeChallengeMethod: 'S256',
        ...(state ? { state } : {}),
      }),
    onSuccess: ({ weiter }) => window.location.assign(weiter),
    onError: (e: Error) => setFehler(e.message),
  })

  const ablehnen = () => {
    const ziel = new URL(redirectUri)
    ziel.searchParams.set('error', 'access_denied')
    if (state) ziel.searchParams.set('state', state)
    window.location.assign(ziel.toString())
  }

  const absage = !vollstaendig
    ? 'Die Anfrage ist unvollständig (OAuth mit PKCE/S256 erwartet). Bitte die Verbindung in Claude neu starten.'
    : error
      ? error.message
      : info && !info.gueltig
        ? 'Dieser Client ist in Work nicht angemeldet oder nennt einen fremden Rückweg. Bitte die Verbindung in Claude neu starten.'
        : null

  return (
    <>
      <Topbar titel="Claude verbinden" untertitel="Zugriff auf deine KI-Lösungen in Work" />
      <Card className="max-w-[560px]">
        <CardPad>
          {absage ? (
            <p className="text-[13px] text-pool-sofort">{absage}</p>
          ) : !info ? (
            <p className="text-[13px] text-muted">Einen Moment …</p>
          ) : !info.darf ? (
            <p className="text-[13px]">
              Die Claude-Anbindung ist für dich noch nicht freigeschaltet. Die Administration setzt
              das Häkchen „🤖 Claude" unter{' '}
              <em>KI &amp; Einstellungen → Team &amp; Welten → Benutzer</em>.
            </p>
          ) : (
            <>
              <h3 className="text-sm font-bold">
                {info.clientName} möchte sich mit NetCon Work verbinden
              </h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-[12.5px] text-ink2">
                <li>
                  Claude sieht <strong>nur</strong> Aufgaben, die du mit „🤖 Für Claude freigeben"
                  markiert hast, und davon nur die, die du selbst sehen darfst.
                </li>
                <li>
                  Claude kann einen Stand und ein Ergebnis hinterlegen und Kommentare{' '}
                  <strong>entwerfen</strong>. Abschicken kannst nur du.
                </li>
                <li>Status, Bearbeiter und Prioritäten bleiben unberührt.</li>
                <li>
                  Trennen jederzeit unter <em>KI &amp; Einstellungen → Zugänge</em>.
                </li>
              </ul>
              <p className="mt-2 text-[11.5px] text-muted">Zurück zu: {info.rueckwegHost}</p>
              {fehler && <p className="mt-2 text-[12.5px] text-pool-sofort">{fehler}</p>}
              <div className="mt-3 flex gap-1.5">
                <Button disabled={erlauben.isPending} onClick={() => erlauben.mutate()}>
                  Erlauben
                </Button>
                <Button variant="ghost" onClick={ablehnen}>
                  Ablehnen
                </Button>
              </div>
            </>
          )}
        </CardPad>
      </Card>
    </>
  )
}
